import { combineEpics, ofType } from "redux-observable";
import { filter, from, map, mergeMap, skipWhile, switchMap, withLatestFrom } from "rxjs";
import { RootState } from "../../../store/ConfigureStore";
import { setFlopValue, setInflowCalculations, setStaticBottomHolePressurePI, setStaticFluidLevelFromSurfacePI2 } from "../../../store/jetlift/jetlift-input/WellSlice";
import { getFlopValue, getGetStatFldChange, getInflowCalculations, getReserPrevChange } from "../../../services/jetlift/jetlift-input/well.service";
import { FLOPObject } from "../../../store/model/calculations/calculation";

const flop = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "jetlift/well/setCasingPressure",
            "jetlift/well/setProductivityIndex",
            "jetlift/well/setStaticBottomHolePressurePI",
            "jetlift/well/setStaticFluidLevelFromSurfacePI",
            "jetlift/well/setStaticBottomHolePressureP",
            "jetlift/well/setFlowingBottomHolePressureP",
            "jetlift/well/setTestFlowRate",
            "jetlift/well/setStaticFluidLevelFromSurfaceHead",
            "jetlift/well/setProducingFluidLevelFromSurfaceHead",
            "jetlift/well/setTestFlowRateHead",
            "jetlift/well/setTestFlowRate",
            "jetlift/well/setDesiredProductionRate",
            "jetlift/well/setSettingDepth",
            "jetlift/well/setPumpSettingVerticalDepth",
            "jetlift/well/setReservoirDatum",
            "jetlift/fluidTab/setWaterCutCombinedSpGr"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        switchMap(([actionData, wellInput, fluidInput, unit]) => {
            let PI = 0;
            let staticBottomHolePressure = 0;
            let staticFluidLvlFromSurface = 0;
            let FlowRate = 0;
            let flowingBottomHolePress = 0;
            let flowingFluidLvlFromSurface = 0;
            const combinedSpGr = fluidInput?.liquidPhaseCombinedSpGr || 0;

            if (+wellInput?.inflowTypeLstVal == 0) {
                PI = wellInput?.piVal || PI;
                staticBottomHolePressure = wellInput?.reservoirPressure || 0;
                staticFluidLvlFromSurface = wellInput?.staticFluidLvl || 0;
            }
            if (+wellInput?.inflowTypeLstVal == 1) {
                FlowRate = wellInput?.flowRate || 0
                flowingBottomHolePress = wellInput?.intakePressure || 0
                staticBottomHolePressure = wellInput?.sbhpForPressre || 0
            }
            if (+wellInput?.inflowTypeLstVal == 2) {
                FlowRate = wellInput?.flowRateForHd || 0
                flowingFluidLvlFromSurface = wellInput?.prdcngFluidLvl || 0
                staticFluidLvlFromSurface = wellInput?.staticFluidLvlForHd || 0
            }

            var fLOPObject = {
                fBHH: +flowingFluidLvlFromSurface,
                fBHHUnit: +unit?.unitPrdcngFluidLvlListVal,
                fBHP: +flowingBottomHolePress,
                fBHPUnit: +unit?.unitIntakePressureListVal,
                flowRate: +FlowRate,
                flowRateUnit: +unit?.unitFlowRateListVal,
                pI: +PI,
                pIUnit: +unit?.unitPIValListVal,
                perforation: +wellInput?.perforation,
                perforationUnit: +unit?.unitPerforationListVal,
                productionRate: +wellInput?.productionRate,
                productionRateUnit: +unit?.unitProductionRateListVal,
                sBHH: +staticFluidLvlFromSurface,
                sBHHUnit: +unit?.unitStaticFluidLvlListVal,
                sBHP: +staticBottomHolePressure,
                sBHPUnit: +unit?.unitStaticFluidLvlListVal,
                tVD: +wellInput?.tvd,
                tVDUnit: +unit?.unitTVDListVal,
                combinedSpGr: +combinedSpGr || 0,
                inflowType: +wellInput?.inflowTypeLstVal,
                casingPressure: +wellInput?.casingPressure || 0,
                casingPressureUnit: +unit?.unitCasingPressureListVal,
                fLOPUnit: +unit?.unitFLOPListVal,
                bubblePointPressure: fluidInput?.bubblePoint || 0,
            } as FLOPObject;
            return from(getFlopValue(fLOPObject)).pipe(
                mergeMap(data => {
                    if (data != 0) {
                        return [setFlopValue(Number(data))];
                    }
                    else {
                        return [setFlopValue(null)];
                    }
                }),
            )
        }
        )
    )
};

const fetchStaticFluidLvl = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "jetlift/well/setCasingPressure",
            "jetlift/well/setReservoirDatum",
            "jetlift/fluidTab/setFluidTabDetails",
            "jetlift/well/setStaticBottomHolePressurePI",
            "jetlift/fluidTab/setWaterCutCombinedSpGr"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        skipWhile(([actionData, wellInput]) => +wellInput?.hiddenPresHeadPIIPType === 1),
        switchMap(([actionData, wellInput, fluidInput, unit]) => {
            const ResvPres = +wellInput?.reservoirPressure;
            const ReserviourDatum = +wellInput?.perforation;
            const pd = {
                ResvPres: (ResvPres || 0),
                CombSpGr: +fluidInput?.combinedSpGr || 0,
                InUnit: +unit?.unitReservoirPressureListVal || 0,
                OutUnit: +unit?.unitStaticFluidLvlListVal,
                Perftn: +(ReserviourDatum || 0),
                PerftnUnit: +unit?.unitPerforationListVal,
                CasingPressure: +wellInput?.casingPressure || 0,
                CasingPressureUnit: +unit?.unitCasingPressureListVal || 0,
            };

            return from(getReserPrevChange(pd)).pipe(
                map(data => setStaticFluidLevelFromSurfacePI2(Number(data))),
            )
        }
        ))
}

const fetchBottomHolePressure = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "jetlift/well/setCasingPressure",
            "jetlift/well/setReservoirDatum",
            "jetlift/well/setStaticFluidLevelFromSurfacePI",
            "jetlift/fluidTab/setWaterCutCombinedSpGr"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        skipWhile(([actionData, wellInput]) => +wellInput?.hiddenPresHeadPIIPType === 0),
        switchMap(([actionData, wellInput, fluidInput, unit]) => {
            const StaticFluidLevelFromSurface = +wellInput?.staticFluidLvl
            const ReserviourDatum = +wellInput?.perforation
            const pd = {
                StacFld: +(StaticFluidLevelFromSurface || 0),
                CombSpGr: +fluidInput.combinedSpGr || 0,
                InUnit: +unit?.unitStaticFluidLvlListVal || 0,
                OutUnit: +unit?.unitReservoirPressureListVal || 0,
                Perftn: +(ReserviourDatum || 0),
                PerftnUnit: +unit?.unitPerforationListVal || 0,
                casingPressure: +wellInput?.casingPressure || 0,
                casingPressureUnit: +unit?.unitCasingPressureListVal || 0,
            }

            return from(getGetStatFldChange(pd)).pipe(
                mergeMap(data => {
                    if (data != 0) {
                        return [setStaticBottomHolePressurePI(Number(data))];
                    }
                    else {
                        return [setStaticBottomHolePressurePI(null)];
                    }
                }),
            )
        }),
    )
};

const fetchWellInfoCalculations = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "jetlift/well/setCasingPressure",
            "jetlift/well/setProductivityIndex",
            "jetlift/well/setStaticBottomHolePressurePI",
            "jetlift/well/setStaticFluidLevelFromSurfacePI2",
            "jetlift/well/setStaticBottomHolePressureP",
            "jetlift/well/setFlowingBottomHolePressureP",
            "jetlift/well/setTestFlowRate",
            "jetlift/well/setStaticFluidLevelFromSurfaceHead",
            "jetlift/well/setProducingFluidLevelFromSurfaceHead",
            "jetlift/well/setTestFlowRateHead",
            "jetlift/well/setDesiredProductionRate",
            "jetlift/well/setSettingDepth",
            "jetlift/well/setPumpSettingVerticalDepth",
            "jetlift/well/setReservoirDatum",
            "jetlift/fluidTab/setWaterCutCombinedSpGr"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        filter(([actionData, tubingDetails, wellDetails, fluidDetails]) =>
            (
                (+wellDetails?.inflowTypeLstVal === 0 && +wellDetails?.piVal > 0 && +wellDetails?.reservoirPressure > 0 && +wellDetails?.staticFluidLvl > 0)
                || (+wellDetails?.inflowTypeLstVal === 1 && +wellDetails?.sbhpForPressre > 0 && +wellDetails?.intakePressure > 0 && +wellDetails?.flowRate > 0)
                || (+wellDetails?.inflowTypeLstVal === 2 && +wellDetails?.staticFluidLvlForHd > 0 && +wellDetails?.prdcngFluidLvl > 0 && +wellDetails?.flowRateForHd > 0)
            )
            && +wellDetails?.productionRate > 0 && +wellDetails?.perforation > 0 && +fluidDetails?.liquidPhaseCombinedSpGr > 0
        ),
        switchMap(([actionData, tubingDetails, wellDetails, fluidDetails, unit]) => {
            let inflowInputModel: object | null = null;
            const inflowType = +(wellDetails?.inflowTypeLstVal) || 0;
            if (inflowType === 0) {
                inflowInputModel = {
                    ProductivityIndex: +(wellDetails?.piVal || 0),
                    ProductivityIndexUnit: +(unit?.unitPIValListVal ?? 0),
                    StaticBottomHolePressure: +(wellDetails?.reservoirPressure ?? 0),
                    StaticBottomHolePressureUnit: +(unit?.unitReservoirPressureListVal ?? 0),
                    StaticFluidLevelFromSurface: +(wellDetails?.staticFluidLvl ?? 0),
                    StaticFluidLevelFromSurfaceUnit: +(unit?.unitStaticFluidLvlListVal ?? 0),
                }
            }
            else if (inflowType === 1) {
                inflowInputModel = {
                    StaticBottomHolePressure: +(wellDetails?.sbhpForPressre ?? 0),
                    StaticBottomHolePressureUnit: +(unit?.unitReservoirPressureListVal ?? 0),
                    FlowingBottomHolePressure: +(wellDetails?.intakePressure ?? 0),
                    FlowingBottomHolePressureUnit: +(unit?.unitIntakePressureListVal ?? 0),
                    FlowRate: +(wellDetails?.flowRate ?? 0),
                    FlowRateUnit: +(unit?.unitFlowRateListVal ?? 0),
                }
            }
            else if (inflowType === 2) {
                inflowInputModel = {
                    StaticFluidLevelFromSurface: +(wellDetails?.staticFluidLvlForHd ?? 0),
                    StaticFluidLevelFromSurfaceUnit: +(unit?.unitStaticFluidLvlListVal ?? 0),
                    ProducingFluidLevelFromSurface: +(wellDetails?.prdcngFluidLvl ?? 0),
                    ProducingFluidLevelFromSurfaceUnit: +(unit?.unitPrdcngFluidLvlListVal ?? 0),
                    FlowRate: +(wellDetails?.flowRateForHd ?? 0),
                    FlowRateUnit: +(unit?.unitFlowRateListVal ?? 0),
                }
            }

            const inflowClaculationInputs = {
                Units: {
                    CasingPressureUnit: +unit?.unitCasingPressureListVal || 0,
                    ProductionRateUnit: +unit?.unitProductionRateListVal || 0,
                    PerforationUnit: +unit?.unitPerforationListVal || 0,
                    ProductivityIndexUnit: +(unit?.unitPIValListVal ?? 0),
                    ProducingFluidLevelFromSurfaceUnit: +(unit?.unitPrdcngFluidLvlListVal ?? 0),
                    StaticBottomHolePressureUnit: +(unit?.unitReservoirPressureListVal ?? 0),
                    StaticFluidLevelFromSurfaceUnit: +(unit?.unitStaticFluidLvlListVal ?? 0),
                    FlowingBottomHolePressureUnit: +(unit?.unitIntakePressureListVal ?? 0),
                    FlowRateUnit: +(unit?.unitFlowRateListVal ?? 0),
                },
                UnitSystem: +tubingDetails?.unitSys,
                InflowType: +wellDetails?.inflowTypeLstVal,
                CasingPressure: +wellDetails?.casingPressure || 0,
                ProductionRate: +wellDetails?.productionRate,
                Perforation: +wellDetails?.perforation,
                LiquidPhaseSpGr: +fluidDetails?.liquidPhaseCombinedSpGr || 0,
                PiInputs: inflowType === 0 ? inflowInputModel : null,
                PressureInflowInputs: inflowType === 1 ? inflowInputModel : null,
                HeadInflowInputs: inflowType === 2 ? inflowInputModel : null
            }

            return from(getInflowCalculations(inflowClaculationInputs)).pipe(
                map(data => setInflowCalculations(data))
            )
        })

    )
};

export const jetLiftWellEpic = combineEpics(
    flop, 
    fetchStaticFluidLvl, 
    fetchBottomHolePressure, 
    fetchWellInfoCalculations
);